// The source document, beside the data that was read out of it.
//
// Spec ch.06 for the Documents screen: "Left inbox, source and proposal side by
// side, highlighted source fields … Immutable source versions and evidence
// spans; validated proposal revisions. Highlight only fields with actual
// extraction provenance." Acceptance V04: "Source highlighting identifies a real
// field/evidence span. Unsupported highlighting shows no fabricated confidence."
//
// This component is that acceptance test, and every rule below exists because
// the easy version of this screen fails it.
//
// ---------------------------------------------------------------------------
// 1. Nothing is drawn that did not come out of the bucket
// ---------------------------------------------------------------------------
//
// The bytes are the stored ones: `POST /documents/{id}/source-url` issues a
// short-lived authorised URL, the app fetches it with its session, and what is
// rendered is the response. There is no fixture invoice and no reconstruction of
// a document from its extracted fields — a drawn invoice would be a picture of
// data we already have, with a reviewer checking our reading against our
// reading.
//
// ---------------------------------------------------------------------------
// 2. A highlight is a claim about a location, so it needs all three of these
// ---------------------------------------------------------------------------
//
// A field is highlighted only when:
//
//   * it HAS a span. `spanStart`/`spanEnd` are null for a field the extractor
//     could not locate, and a null must stay unhighlighted — a fabricated (0,0)
//     would draw a box around the first characters of the document with
//     complete conviction.
//   * the span is into THIS version. An offset is an offset into the text of one
//     immutable version. If the extraction ran over a different version than the
//     one on screen, every offset is wrong by however much the scan changed, so
//     nothing is highlighted and the screen says why.
//   * the source is rendered as TEXT, or it is a PDF and the evidenced
//     characters are found on the rendered page exactly once. A span is a
//     character offset into the text the model read; for a PDF that text was
//     made on the server and the browser never sees it, so the offsets cannot
//     be used directly. pdf.js renders the page with its own text layer
//     (PdfView), and the excerpt the worker recorded beside the span (0031) is
//     looked for in it — see evidence.ts. Not found, or found twice, and the
//     field is listed with no location: an absence, not an approximation.
//   * or the source was read by OCR (a photo, a scan) and the worker recorded
//     the span's boxes (0034): the words Tesseract placed under exactly the
//     characters the span covers. Those are drawn over the image, and nothing
//     else is — an OCR source without boxes is shown unmarked.
//
// ---------------------------------------------------------------------------
// 3. A span is the region that EVIDENCES a field, not the field's value
// ---------------------------------------------------------------------------
//
// For a labelled value the span is the value; for a value read out of a table
// row the span is the row. Both are correct evidence, so a highlight is a marked
// region labelled with the fields that point into it — never a marked region
// presented as being the value. Two fields may also point into one stretch of
// text, which is why `segmentSource` sweeps boundaries instead of wrapping each
// span in turn: overlapping <mark> elements cannot be nested, and the naive
// version silently drops one of them.
//
// ---------------------------------------------------------------------------
// 4. Confidence is shown only where it was measured
// ---------------------------------------------------------------------------
//
// `confidence` is null for every field the deterministic extractor produces,
// because it is a parser and measures none. Null is rendered as "not measured"
// and never as 0%, a dash in a bar or an empty meter — each of which reads as a
// low score rather than as no score.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, request, requestSource, tenantPath } from '../api/client.ts';
import type { DocumentDetail, ExtractedField, SourceUrl } from '../api/types.ts';
import * as format from '../format.ts';
import { useT, type MessageKey, type Translate } from '../i18n/index.tsx';
import type { EvidenceLocation } from './evidence.ts';
import { PdfView } from './PdfView.tsx';
import { REPLAY } from './replay.ts';

/** The media types this app renders as text, and can therefore highlight. */
const TEXT_MEDIA_TYPES = ['text/plain', 'application/xml'];
/** Rendered by pdf.js, with a text layer the evidence is looked for in. */
const PDF_MEDIA_TYPE = 'application/pdf';
const IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg'];

export interface SourceViewerProps {
  readonly tenantId: string;
  readonly documentId: string;
  readonly token: string;
  /** Newest first, as the document endpoint returns them. */
  readonly versions: DocumentDetail['history']['versions'];
  readonly extraction: DocumentDetail['extraction'];
  /** The document is being read right now: the beam sweeps the page until it is done. */
  readonly scanning?: boolean;
  /**
   * A replay of the finished reading is playing: only the first `reveal`
   * fields (in reading order) are shown and lit. Null when no replay plays.
   */
  readonly reveal?: number | null;
  /** Bumped to restart the beam's single sweep for a replay. */
  readonly replayRun?: number;
  /** Drawn under the field list (the checks). */
  readonly aside?: ReactNode;
}

type ViewerState =
  | { readonly kind: 'loading' }
  /** Nothing can be shown, and the reason is something a person can act on. */
  | {
      readonly kind: 'unavailable';
      readonly reason: MessageKey;
      /** A message key, or text as the server sent it. */
      readonly detail?: { readonly key: MessageKey } | { readonly text: string };
    }
  | {
      readonly kind: 'ready';
      readonly issued: SourceUrl;
      readonly bytes: Uint8Array;
      readonly mediaType: string;
    };

/**
 * One stretch of the source, and every field whose evidence span covers it.
 *
 * `fields` empty means ordinary text. One or more means a marked region, and
 * more than one means two fields evidenced by the same characters — which
 * happens, and which is why this is a list.
 */
export interface SourceSegment {
  readonly text: string;
  readonly fields: readonly string[];
}

/**
 * The source text cut into segments at every span boundary.
 *
 * A sweep rather than a wrap, for the reason in note 3: spans overlap, and
 * wrapping each one in turn produces either nested marks or a lost highlight.
 * Out-of-range and empty spans are dropped rather than clamped — a span that
 * does not fit this text is evidence about some other text, and clamping it
 * would move it somewhere it never pointed.
 */
export function segmentSource(
  text: string,
  fields: readonly ExtractedField[],
): readonly SourceSegment[] {
  const spans = fields.filter(
    (field): field is ExtractedField & { spanStart: number; spanEnd: number } =>
      field.spanStart !== null &&
      field.spanEnd !== null &&
      field.spanStart >= 0 &&
      field.spanEnd > field.spanStart &&
      field.spanEnd <= text.length,
  );
  if (spans.length === 0) return text === '' ? [] : [{ text, fields: [] }];

  const boundaries = [...new Set([0, text.length, ...spans.flatMap((f) => [f.spanStart, f.spanEnd])])]
    .sort((a, b) => a - b);

  const segments: SourceSegment[] = [];
  for (let index = 0; index + 1 < boundaries.length; index += 1) {
    const start = boundaries[index] as number;
    const end = boundaries[index + 1] as number;
    segments.push({
      text: text.slice(start, end),
      fields: spans
        .filter((field) => field.spanStart <= start && field.spanEnd >= end)
        .map((field) => field.fieldPath),
    });
  }
  return segments;
}

export function SourceViewer({
  tenantId,
  documentId,
  versions,
  extraction,
  token,
  scanning = false,
  reveal = null,
  replayRun = 0,
  aside,
}: SourceViewerProps): ReactNode {
  const t = useT();
  const [state, setState] = useState<ViewerState>({ kind: 'loading' });
  const [focused, setFocused] = useState<string | null>(null);
  const [located, setLocated] = useState<ReadonlyMap<string, EvidenceLocation> | null>(null);
  const [fullSize, setFullSize] = useState(false);

  // WHICH version to show, and the order of preference is the point: the one
  // the extraction read, so that its spans are offsets into what is on screen.
  // Only if that version is not among the document's own — it was superseded and
  // the endpoint no longer lists it — does this fall back to the newest, and
  // then the spans are deliberately not used.
  //
  // Keyed by the version's ID, not the object: the document is polled while it
  // is being read, every poll brings a fresh `versions` array, and keying on
  // the object re-fetched the bytes and redrew the PDF on every poll (measured:
  // four fetches and eight canvases in twelve seconds). The source of a version
  // is immutable, so one fetch per version is all there is to know.
  const wantedId =
    versions.find((version) => version.id === extraction?.documentVersionId)?.id ?? versions[0]?.id;

  const load = useCallback(async () => {
    const wanted = wantedId === undefined ? undefined : { id: wantedId };
    if (wanted === undefined) {
      setState({
        kind: 'unavailable',
        reason: 'source.noVersion',
      });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const issued = await request<SourceUrl>(
        tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/source-url`),
        { method: 'POST', token, body: { versionId: wanted.id } },
      );
      const source = await requestSource(issued.url, { token });
      setState({ kind: 'ready', issued, bytes: source.bytes, mediaType: source.mediaType });
    } catch (caught) {
      setState({ kind: 'unavailable', ...unavailable(caught) });
    }
  }, [tenantId, documentId, token, wantedId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'loading') {
    return <p className="muted small">{t('source.opening')}</p>;
  }
  if (state.kind === 'unavailable') {
    return (
      <div className="inline-hint" data-testid="source-unavailable">
        <b>{t(state.reason)}</b>
        {state.detail === undefined ? null : (
          <>
            <br />
            {'key' in state.detail ? t(state.detail.key) : state.detail.text}
          </>
        )}
        <br />
        <button type="button" className="btn text" onClick={() => void load()}>
          {t('source.tryAgain')}
        </button>
      </div>
    );
  }

  // Highlighting is allowed only if all three conditions in note 2 hold. Each is
  // a separate sentence to the reviewer, because "nothing is highlighted" for a
  // PDF and for a superseded extraction are different facts about this document.
  const rendersAsText = TEXT_MEDIA_TYPES.includes(state.mediaType);
  const isPdf = state.mediaType === PDF_MEDIA_TYPE;
  const isImage = IMAGE_MEDIA_TYPES.includes(state.mediaType);
  const sameVersion = extraction !== null && extraction.documentVersionId === state.issued.version.id;
  const fields = extraction?.fields ?? [];
  const highlightable = rendersAsText && sameVersion;
  // A PDF can mark only the fields whose excerpt was recorded (0031).
  const pdfFields = isPdf && sameVersion ? fields.filter((field) => field.spanStart !== null && field.evidenceText !== null) : [];
  const pdfLocated = isPdf && sameVersion ? located : null;
  const anyLocated = pdfLocated !== null && [...pdfLocated.values()].some((entry) => entry.kind === 'located');

  // The design's list: the headline fields, in reading order; everything else
  // recorded is one click away, never dropped. A replay lights the headline
  // fields one by one; otherwise every placed field is lit.
  const { headline, more } = splitFields(fields);
  const lit = reveal === null ? null : new Set(headline.slice(0, reveal).map((field) => field.fieldPath));
  const boxed = sameVersion ? fields.filter((field) => field.spanStart !== null && (field.evidenceBoxes?.length ?? 0) > 0) : [];

  return (
    <div className="read" data-testid="source-viewer">
      <div className="read-page">
        <div
          className={`paper-wrap${scanning ? ' scanning' : ''}${reveal !== null ? ' replaying' : ''}${isPdf || isImage ? ' zoomable' : ''}`}
          data-testid="source-page"
          {...(isPdf || isImage
            ? {
                role: 'button',
                tabIndex: 0,
                'aria-label': t('source.openFullSize'),
                onClick: () => setFullSize(true),
                onKeyDown: (event: { key: string; preventDefault: () => void }) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setFullSize(true);
                  }
                },
              }
            : {})}
        >
          {isPdf ? (
            <PdfView bytes={state.bytes} fields={pdfFields} boxed={boxed} lit={lit} focused={focused} onLocated={setLocated} />
          ) : (
            <SourceFrame
              bytes={state.bytes}
              mediaType={state.mediaType}
              fields={highlightable ? fields : []}
              boxed={boxed}
              lit={lit}
              focused={focused}
            />
          )}
          {/* The beam: a live sweep while the document is really being read; a
              single one, restarted per replay, when the record is played back. */}
          {scanning ? <span className="beam beam-live" aria-hidden="true" /> : null}
          {!scanning && reveal !== null ? <span key={replayRun} className="beam beam-once" aria-hidden="true" /> : null}
          {isPdf || isImage ? (
            <span className="zoom-hint" aria-hidden="true">
              {t('source.zoomHint')}
            </span>
          ) : null}
        </div>
        {fullSize ? (
          <FullSize onClose={() => setFullSize(false)}>
            {isPdf ? (
              <PdfView bytes={state.bytes} fields={pdfFields} boxed={boxed} focused={focused} onLocated={() => undefined} crop={false} />
            ) : (
              <SourceFrame bytes={state.bytes} mediaType={state.mediaType} fields={[]} boxed={boxed} lit={null} focused={focused} />
            )}
          </FullSize>
        ) : null}

        <p className="source-legend" data-testid="source-legend">
          {t('source.legend.version', {
            version: state.issued.version.version,
            mediaType: state.mediaType,
            bytes: state.issued.version.byteSize,
          })}
          {/* Shown only when the server has one. Null means either that this
              media type has no pages — text has none, rather than an unknown
              number — or that the counter could not read the file, and a
              document in the second case is quarantined and says so elsewhere.
              "0 pages" would collapse the two into a measurement. */}
          {state.issued.version.pageCount === null
            ? ''
            : ` · ${t('source.legend.pages', { count: state.issued.version.pageCount })}`}
          .{' '}
          {extraction === null
            ? scanning
              ? t('source.legend.scanning')
              : t('source.legend.notExtracted')
            : highlightable
              ? t('source.legend.highlighted')
              : !sameVersion
                ? t('source.legend.otherVersion')
                : boxed.length > 0
                  ? t('source.legend.ocrBoxes')
                  : isPdf
                    ? anyLocated
                      ? t('source.legend.pdfLocated')
                      : pdfFields.length === 0
                        ? t('source.legend.pdfNoExcerpt')
                        : t('source.legend.pdfNoneFound')
                    : t('source.legend.imageNoPlace', { mediaType: state.mediaType })}
        </p>
      </div>

      <div className="read-side">
        <h3 className="read-title">
          {t(state.issued.version.pageCount === 1 ? 'source.heading.page' : 'source.heading.document')}
        </h3>
        {fields.length === 0 ? null : <p className="confidence-note" data-testid="confidence-note">{confidenceNote(fields, t)}</p>}
        {fields.length === 0 ? (
          <p className="muted small">
            {t(scanning ? 'source.reading' : extraction === null ? 'source.nothingRead' : 'source.noFields')}
          </p>
        ) : (
          <>
            <ExtractedFields
              fields={headline}
              highlightable={highlightable}
              pdfLocated={pdfLocated}
              boxed={new Set(boxed.map((field) => field.fieldPath))}
              reveal={reveal}
              focused={focused}
              onFocus={setFocused}
            />
            {more.length === 0 || reveal !== null ? null : (
              <details className="more-fields">
                <summary>
                  {t('source.moreFields', { count: more.length })}
                </summary>
                <ExtractedFields
                  fields={more}
                  highlightable={highlightable}
                  pdfLocated={pdfLocated}
                  boxed={new Set(boxed.map((field) => field.fieldPath))}
                  reveal={null}
                  focused={focused}
                  onFocus={setFocused}
                />
              </details>
            )}
          </>
        )}
        {aside}
      </div>
    </div>
  );
}

/**
 * The bytes, rendered by what the SERVER says they are.
 *
 * The media type comes from the response header, which comes from the sniff
 * packages/storage performed on the stored bytes. Nothing here guesses from a
 * name, and an unexpected type is reported rather than rendered — there is no
 * "try it as text and see", because that is how a file that is not what it
 * claims gets interpreted by whatever is willing to interpret it.
 */
function SourceFrame({
  bytes,
  mediaType,
  fields,
  boxed,
  lit,
  focused,
}: {
  readonly bytes: Uint8Array;
  readonly mediaType: string;
  readonly fields: readonly ExtractedField[];
  /** OCR fields with their recorded boxes on the image (0034). */
  readonly boxed: readonly ExtractedField[];
  /** During a replay, the fields lit so far; null when every placed field is lit. */
  readonly lit: ReadonlySet<string> | null;
  readonly focused: string | null;
}): ReactNode {
  const t = useT();
  const framed = IMAGE_MEDIA_TYPES.includes(mediaType);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!framed) return undefined;
    // A blob typed with the SERVER's media type, which is what keeps this safe
    // rather than the frame's attributes: a blob of type application/pdf or
    // image/png is handed to the PDF viewer or the image decoder, and the one
    // type that would execute — HTML — is refused at intake and cannot arrive
    // here. See the sandbox note below for what the frame itself does and does
    // not buy.
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mediaType }));
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl(null);
    };
  }, [bytes, mediaType, framed]);

  if (TEXT_MEDIA_TYPES.includes(mediaType)) {
    const text = new TextDecoder().decode(bytes);
    return (
      <div className="invoice">
        <pre className="source-text" data-testid="source-text">
          {segmentSource(text, fields).map((segment, index) =>
            segment.fields.length === 0 ? (
              <span key={index}>{segment.text}</span>
            ) : (
              <mark
                key={index}
                className={`source-value${
                  focused !== null && segment.fields.includes(focused) ? ' focused' : ''
                }${lit === null || segment.fields.some((field) => lit.has(field)) ? ' lit' : ''}`}
                // The fields this region is the evidence for — plural, because
                // one stretch of text can evidence more than one.
                data-fields={segment.fields.join(' ')}
                title={segment.fields.join(', ')}
              >
                {segment.text}
              </mark>
            ),
          )}
        </pre>
      </div>
    );
  }

  if (framed && objectUrl !== null) {
    return (
      <div className="invoice">
        <div className="source-image-wrap">
          <img className="source-image" src={objectUrl} alt={t('source.imageAlt')} />
          <EvidenceBoxes fields={boxed} page={1} lit={lit} focused={focused} />
        </div>
      </div>
    );
  }
  if (framed) return <p className="muted small">{t('source.preparing')}</p>;

  return (
    <div className="inline-hint" data-testid="source-unrenderable">
      {t('source.unrenderable', { mediaType })}
    </div>
  );
}

/**
 * The source at full size, over the screen, with the same highlights. A
 * dialog: Escape or the close button returns, and focus goes to the close
 * button so a keyboard user is not left behind the overlay.
 */
function FullSize({ children, onClose }: { readonly children: ReactNode; readonly onClose: () => void }): ReactNode {
  const t = useT();
  const close = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    close.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fullsize" role="dialog" aria-modal="true" aria-label={t('source.fullSize')} onClick={onClose}>
      <div className="fullsize-sheet" onClick={(event) => event.stopPropagation()}>
        <button ref={close} type="button" className="btn fullsize-close" onClick={onClose}>
          {t('source.close')}
        </button>
        {children}
      </div>
    </div>
  );
}

/**
 * The recorded boxes of the fields on one page, over the page as drawn. A box
 * is where OCR read the characters the field's span covers — positioned as a
 * fraction of the page, so it lands on the same words at any size.
 */
export function EvidenceBoxes({
  fields,
  page,
  lit,
  focused,
}: {
  readonly fields: readonly ExtractedField[];
  readonly page: number;
  readonly lit: ReadonlySet<string> | null;
  readonly focused: string | null;
}): ReactNode {
  const boxes = fields.flatMap((field) =>
    (field.evidenceBoxes ?? [])
      .filter((box) => box.page === page)
      .map((box, index) => ({ field: field.fieldPath, box, key: `${field.fieldPath}:${index}` })),
  );
  if (boxes.length === 0) return null;
  return (
    <div className="ocr-boxes" aria-hidden="true">
      {boxes.map(({ field, box, key }) => (
        <span
          key={key}
          className={`source-value ocr-box${focused === field ? ' focused' : ''}${lit === null || lit.has(field) ? ' lit' : ''}`}
          data-fields={field}
          title={field}
          style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }}
        />
      ))}
    </div>
  );
}

/**
 * Every field the extraction recorded, and what is actually known about each.
 *
 * All of them, not only the ones that became the proposal: ch.02 wants the
 * extraction persisted as it was, and a reviewer comparing our reading with the
 * paper needs to see a field we read and did not use. Hovering or focusing a row
 * marks its evidence in the source, which is the whole point of the pairing.
 */
function ExtractedFields({
  fields,
  highlightable,
  pdfLocated,
  boxed,
  reveal,
  focused,
  onFocus,
}: {
  readonly fields: readonly ExtractedField[];
  readonly highlightable: boolean;
  /** For a PDF of the extracted version: where each excerpt was found. Null otherwise. */
  readonly pdfLocated: ReadonlyMap<string, EvidenceLocation> | null;
  /** Fields drawn on an OCR image by their recorded boxes. */
  readonly boxed: ReadonlySet<string>;
  /** During a replay, how many fields have appeared; null when all are shown. */
  readonly reveal: number | null;
  readonly focused: string | null;
  readonly onFocus: (fieldPath: string | null) => void;
}): ReactNode {
  const t = useT();
  return (
    <ul className="read-fields">
      {fields.map((field, index) => {
        const located = field.spanStart !== null && field.spanEnd !== null;
        const shown = reveal === null || index < reveal;
        // The newest field of a replay types in; the rest are already there.
        const typing = reveal !== null && index === reveal - 1;
        const value = field.value === null ? '—' : fieldValue(field.fieldPath, field.value);
        return (
          <li
            key={field.fieldPath}
            className={`${focused === field.fieldPath ? 'selected' : ''}${shown ? ' on' : ' pending'}`}
            aria-hidden={shown ? undefined : true}
            onMouseEnter={() => onFocus(field.fieldPath)}
            onMouseLeave={() => onFocus(null)}
            onFocus={() => onFocus(field.fieldPath)}
            onBlur={() => onFocus(null)}
            tabIndex={shown ? 0 : -1}
            data-testid={`field-${field.fieldPath}`}
          >
            <span className="read-label" title={field.fieldPath}>
              {fieldLabel(field.fieldPath, t)}
            </span>
            <span className="read-value">
              {/* An empty value is a field the extraction reported as absent,
                  and "—" says that without implying we read a blank. */}
              <b>{typing ? <TypeIn text={value} /> : value}</b>
              <small>
                {field.derivedFrom != null ? (
                  <span data-testid={`derived-${field.fieldPath}`}>
                    {t('source.derived', { how: t(DERIVED_FROM[field.derivedFrom]) })}
                  </span>
                ) : !located ? (
                  <span className="muted">{t('source.noPlace')}</span>
                ) : boxed.has(field.fieldPath) ? (
                  <span>{t('source.foundOnPage', { page: field.page ?? 1 })}</span>
                ) : pdfLocated !== null ? (
                  <PdfEvidence field={field} location={pdfLocated.get(field.fieldPath)} />
                ) : !highlightable ? (
                  <span className="muted">
                    {field.page === null ? t('source.spanNotShown') : t('source.pageNotShown', { page: field.page })}
                  </span>
                ) : (
                  <span>{field.page === null ? t('source.found') : t('source.foundOnPage', { page: field.page })}</span>
                )}
                {/* V04: no fabricated confidence. A measured one is shown as
                    measured; an unmeasured one shows nothing here — the list
                    says once, above, which fields were not measured — and
                    never a 0% or an empty bar that would read as a low score. */}
                {field.confidence === null ? null : <span> · {t('source.confidence.value', { percent: percent(field.confidence) })}</span>}
              </small>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Confidence, said once for the list. The model measures none (a language
 * model's self-reported number is not a measurement), so this usually says
 * that of every field; where some were measured, they show it on their row.
 */
function confidenceNote(fields: readonly ExtractedField[], t: Translate): string {
  const unmeasured = fields.filter((field) => field.confidence === null).length;
  if (unmeasured === fields.length) return t('source.confidence.none');
  if (unmeasured === 0) return t('source.confidence.all');
  return t('source.confidence.some', { unmeasured, total: fields.length });
}

/** The design's typewriter: the value appears over ~260 ms. The full text is the accessible name throughout. */
function TypeIn({ text }: { readonly text: string }): ReactNode {
  const [count, setCount] = useState(0);
  useEffect(() => {
    setCount(0);
    const step = Math.max(14, Math.floor(REPLAY.typing / Math.max(1, text.length)));
    const timer = setInterval(() => {
      setCount((current) => {
        if (current >= text.length) clearInterval(timer);
        return Math.min(text.length, current + 1);
      });
    }, step);
    return () => clearInterval(timer);
  }, [text]);
  return (
    <span aria-label={text}>
      <span aria-hidden="true">{text.slice(0, count)}</span>
    </span>
  );
}

/**
 * The fields the design lists, in the design's order: supplier, VAT number,
 * invoice number, invoice date, due date, the line(s), net, VAT, total.
 */
function headlineRank(path: string): number | undefined {
  const fixed = ['supplierName', 'supplierVatNumber', 'invoiceNumber', 'invoiceDate', 'dueDate'].indexOf(path);
  if (fixed !== -1) return fixed;
  const line = /^lines\[(\d+)\]\.description$/.exec(path);
  if (line !== null) return 10 + Number(line[1]) / 1000;
  return { totalNet: 20, totalTax: 21, total: 22 }[path];
}

/**
 * The headline fields with a value, in the design's order, and the rest —
 * values that were read but are not headline, and the fields read as absent.
 * Exported because the replay counts the headline fields.
 */
export function splitFields(fields: readonly ExtractedField[]): {
  readonly headline: readonly ExtractedField[];
  readonly more: readonly ExtractedField[];
} {
  const headline = fields
    .filter((field) => field.value !== null && headlineRank(field.fieldPath) !== undefined)
    .sort((a, b) => (headlineRank(a.fieldPath) ?? 0) - (headlineRank(b.fieldPath) ?? 0));
  const more = fields.filter((field) => !headline.includes(field));
  return { headline, more };
}

/** How our code derived a value the model did not report (0036, 0037, 0038). */
const DERIVED_FROM: Readonly<Record<'amount_markers' | 'vat_breakdown' | 'arithmetic' | 'person', MessageKey>> = {
  amount_markers: 'source.derived.amount_markers',
  vat_breakdown: 'source.derived.vat_breakdown',
  arithmetic: 'source.derived.arithmetic',
  person: 'source.derived.person',
};

/** The label of each canonical field name; the name itself is never translated. */
const FIELD_LABELS: Readonly<Record<string, MessageKey>> = {
  documentType: 'source.field.documentType',
  supplierName: 'source.field.supplierName',
  supplierVatNumber: 'source.field.supplierVatNumber',
  supplierRegistrationNumber: 'source.field.supplierRegistrationNumber',
  invoiceNumber: 'source.field.invoiceNumber',
  invoiceDate: 'source.field.invoiceDate',
  dueDate: 'source.field.dueDate',
  currency: 'source.field.currency',
  paymentMethod: 'source.field.paymentMethod',
  originalInvoiceNumber: 'source.field.originalInvoiceNumber',
  totalNet: 'source.field.totalNet',
  totalTax: 'source.field.totalTax',
  total: 'source.field.total',
};

const LINE_LABELS: Readonly<Record<string, MessageKey>> = {
  description: 'source.line.description',
  quantity: 'source.line.quantity',
  unitPrice: 'source.line.unitPrice',
  net: 'source.line.net',
  taxRate: 'source.line.taxRate',
};

/** "lines[0].net" → "Line 1 net"; the path stays in the title for whoever needs it. */
function fieldLabel(path: string, t: Translate): string {
  const line = /^lines\[(\d+)\]\.(\w+)$/.exec(path);
  if (line !== null) {
    const part = LINE_LABELS[line[2] ?? ''];
    return t('source.field.line', { n: Number(line[1]) + 1, part: part === undefined ? (line[2] ?? '') : t(part) });
  }
  const vat = /^taxBreakdown\[(\d+)\]\.(\w+)$/.exec(path);
  if (vat !== null) return t('source.field.vatRow', { n: Number(vat[1]) + 1, part: vat[2] ?? '' });
  const label = FIELD_LABELS[path];
  return label === undefined ? path : t(label);
}

/** Amounts and dates in Dutch notation; everything else exactly as read. */
function fieldValue(path: string, value: string): string {
  if (/^-?\d+(\.\d+)?$/.test(value) && /(net|tax|total|unitPrice|amount)$/i.test(path)) return format.decimal(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(value) && /date$/i.test(path)) return format.date(value);
  return value;
}

/** A PDF field's evidence cell: where its excerpt was found on the page, or why not. */
function PdfEvidence({
  field,
  location,
}: {
  readonly field: ExtractedField;
  readonly location: EvidenceLocation | undefined;
}): ReactNode {
  const t = useT();
  if (field.evidenceText === null) {
    return <span className="muted">{t('source.pdf.noText')}</span>;
  }
  if (location === undefined) return <span className="muted">{t('source.pdf.looking')}</span>;
  if (location.kind === 'located') return <span>{t('source.foundOnPage', { page: location.page })}</span>;
  if (location.kind === 'ambiguous') {
    return <span className="muted">{t('source.pdf.ambiguous')}</span>;
  }
  return <span className="muted">{t('source.pdf.notFound')}</span>;
}

/**
 * A measured confidence as a percentage, from the decimal string the API sent.
 *
 * Formatted for display only. ch.02: "Do not use JavaScript floating-point
 * arithmetic for financial decisions" — a confidence is not money, but nothing
 * is decided from this number here either: the API sends the string the database
 * holds, and the thresholds that act on it live in packages/domain.
 */
function percent(confidence: string): string {
  const value = Number(confidence);
  return Number.isFinite(value) ? `${(value * 100).toFixed(0)}%` : confidence;
}

/**
 * Why there is nothing to show, in words a reviewer can act on.
 *
 * The two 422 codes are the ones worth distinguishing: they mean the document is
 * genuinely without stored bytes, which is a real state for anything that
 * arrived before private storage existed. Everything else is reported as
 * uncertainty rather than as an answer — in particular a 404, which conflates a
 * document that is no longer visible to this session with a deployment that
 * issues no viewer URLs at all, and must not be rendered as "there is nothing
 * there".
 */
function unavailable(caught: unknown): Omit<Extract<ViewerState, { kind: 'unavailable' }>, 'kind'> {
  if (!(caught instanceof ApiError)) {
    return { reason: 'source.couldNotOpen' };
  }
  const codes = caught.fieldErrors.map((error) => error.code);
  if (codes.includes('no_stored_object') || codes.includes('no_version')) {
    return { reason: 'source.noStored', detail: { key: 'source.noStoredDetail' } };
  }
  if (caught.status === 0) {
    return { reason: 'source.unreachable', detail: { key: 'source.unreachableDetail' } };
  }
  if (caught.isAbsent) {
    return { reason: 'source.absent', detail: { key: 'source.absentDetail' } };
  }
  // The server's own message, shown as it was sent.
  return { reason: 'source.couldNotOpen', detail: { text: caught.message } };
}
